import { DATA_TYPE, INDEX, VALUE_TYPE } from "./contanst";
import { Modal, Search, Select, Tabs, Tooltip } from "@gw/web-basic-components";
import NodeEditor from "../node-editor";
import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DotLabel from "../dot-label";
import { NodeEditorProps, NodeEditorRef, Node } from "../node-editor/dto";
import {
  flattenFields,
  formulaInputs,
  isOperator,
  validateMathFormula,
} from "./utils";
import { DataType, Field, TableEditorProps } from "./dto";
import { chunk } from "lodash";
import { cn, isFunction } from "../utils";
import useFlattenFields from "./hooks/use-flatten-fields";

type ValueType = {
  type: "text" | "dynamic" | "number" | "operator";
  value: string;
  label: string;
}[];

type ValueEditorConfig = (Omit<
  TableEditorProps["fields"][number],
  "children"
> & {
  disabled?: boolean;
  tooltip?: string;
  children: ValueEditorConfig;
})[];

export type ValueEditorProps = {
  value?: ValueType;
  onChange?: (value: ValueType) => void;
  type: DataType;
  config: ValueEditorConfig;
  disabled?: boolean | ((current: ValueType, item: Field) => boolean);
  trigger?: ReactElement | ((onClick: () => void) => ReactElement);
  children?: ReactNode;
  required?: boolean;
};

// 是否为数字的标签
const isNumberTag = (node: Node, fields: string[]) =>
  node.type === "tag" &&
  !isOperator(node.content) &&
  !fields.includes(node.content) &&
  node.content !== INDEX.code;
// 格式化节点
const formatValue = (value: ValueType): Node[] => {
  return value?.map((i, idx) => ({
    type: i.type === "text" ? "text" : "tag",
    content: i.value,
    id: idx.toString(),
  }));
};
// 转换节点
const transformValue = (
  value: Node[],
  fields: Omit<Field, "children">[]
): ValueType => {
  return value.map((i) => {
    if (i.type === "tag") {
      const field = fields.find((j) => j.code === i.content);
      const operator = isOperator(i.content);
      const dynamic = !!field;
      let type: ValueType[0]["type"] = "number";
      if (dynamic) type = "dynamic";
      if (operator) type = "operator";
      return {
        type,
        label: field?.name ?? i.content,
        value: i.content,
      };
    }
    return {
      type: "text",
      label: i.content,
      value: i.content,
    };
  });
};
// 递归过滤节点
const filterNodes = (nodes: Field[], searchValue: string) => {
  return nodes
    .map((node) => {
      // 如果有子节点，先处理子节点
      if (node.children && node.children.length > 0) {
        const filteredChildren = filterNodes(node.children, searchValue);
        // 如果子节点全部被过滤掉了，则当前节点也应该被过滤掉
        if (filteredChildren.length === 0) {
          return null;
        }
        // 保留有有效子节点的节点
        return {
          ...node,
          children: filteredChildren,
        };
      }
      // 如果是叶子节点，检查是否匹配搜索值
      return node.name.includes(searchValue) ? node : null;
    })
    .filter((node) => node !== null); // 过滤掉null值
};

// 给循环的字段添加序号
const withIndexConfig = (config: Field[]): Field[] => {
  const ret: Field[] = [];
  let shouldPushIndex = 0;
  for (const i of config) {
    if (i.children && i.children.length > 0) {
      ret.push({ ...i, children: withIndexConfig(i.children) });
    } else {
      ret.push(i);
      if (i.isList) {
        shouldPushIndex++;
      }
    }
  }
  // 每个子项都有才添加序号字段
  if (shouldPushIndex === ret.length) {
    const { parentCode } = ret[0] ?? {};
    ret.push({
      name: INDEX.content,
      code: `${parentCode}_${INDEX.code}`,
      type: VALUE_TYPE.NUMBER,
      isList: true,
      children: [],
      parentCode,
      formula: [],
    });
  }
  return ret;
};

const ValueEditor = (props: ValueEditorProps) => {
  const {
    type = DATA_TYPE.DYNAMIC,
    value,
    config,
    disabled,
    onChange,
    trigger,
    children,
    required,
  } = props;
  const [open, setOpen] = useState(false);
  // 公式节点
  const [nodes, setNodes] = useState<Node[]>([]);
  // 搜索内容
  const [search, setSearch] = useState("");
  // 选择tab
  const [activeKey, setActiveKey] = useState<string>();
  // 高亮字段
  const [activeField, setActiveField] = useState<string>();

  const nodeEditorRef = useRef<NodeEditorRef>();

  const cfg = useMemo(() => withIndexConfig(config), [JSON.stringify(config)]);

  const [fields, fieldsCodes] = useFlattenFields(cfg);
  // 必填校验
  const [requiredError, setRequiredError] = useState(false);
  // 所有可选字段dom
  const fieldRefs = useRef<{ [key: string]: HTMLSpanElement }>({});

  // 不合法的公式节点
  const errorIds = useMemo(
    () =>
      type === DATA_TYPE.FORMULA
        ? [
            ...validateMathFormula(nodes, fieldsCodes),
            ...nodes.filter((i) => i.type === "text").map((i) => i.id),
          ]
        : [],
    [JSON.stringify(nodes), type, JSON.stringify(fieldsCodes)]
  );
  // 转换后的value
  const values = useMemo(
    () => transformValue(nodes, fields),
    [JSON.stringify(nodes), fields]
  );
  // 搜索筛选后的选项
  const searchResult = useMemo(() => {
    if (!search) return cfg;
    return filterNodes(cfg, search);
  }, [search, cfg]);
  // 传给节点编辑器的value
  const showValue = formatValue(value) ?? [];
  // 是否为单选模式
  const isDynamic = type === DATA_TYPE.DYNAMIC;

  const openValueEditor = () => {
    setOpen(true);
  };

  const setActiveNode = (node: Node) => {
    if (node.type !== "tag") return;
    const activeTabKey = searchResult
      .map((i) => ({
        ...i,
        fields: flattenFields(i.children),
      }))
      .find((i) => i.fields.find((j) => j.code === node.content))?.code;
    if (activeTabKey) {
      setActiveKey(activeTabKey);
      setActiveField(node.content);
      setTimeout(() => {
        fieldRefs.current[node.content]?.scrollIntoView({
          block: "end",
          inline: "nearest",
        });
      });
    }
  };

  useEffect(() => {
    setActiveKey(searchResult?.[0]?.code);
    setActiveField(undefined);
  }, [searchResult]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setNodes(showValue);
    setRequiredError(false);
    if (showValue?.length === 1) {
      setActiveNode(showValue[0]);
    } else {
      setActiveField(undefined);
    }
  }, [open]);

  // 标签节点显示
  const renderTagNode: NodeEditorProps["renderTagNode"] = (node) =>
    fields.find((i) => i.code === node.content)?.name ??
    value?.find((i) => i.value === node.content)?.label ??
    node.content;

  // 触发器
  const renderTrigger = () => {
    const triggerDisabled = typeof disabled === "boolean" && disabled;
    const click = () => {
      if (!triggerDisabled) {
        openValueEditor();
      }
    };
    // 自定义触发器
    if (trigger) {
      if (isFunction(trigger)) {
        return trigger(click);
      }
      if (isValidElement<{ onClick: () => void }>(trigger)) {
        return cloneElement<{ onClick: () => void }>(trigger, {
          onClick: (...args) => {
            trigger.props.onClick?.(...args);
            click();
          },
        });
      }
    }
    // 默认触发器
    if (isDynamic) {
      const v = value?.[0]?.value;
      const opts = fields
        .filter((i) => i.code === v)
        .map((i) => ({ label: i.name, value: i.code }));
      return (
        <Select
          placeholder="请选择字段取值"
          open={false}
          onClick={click}
          value={value?.[0]?.value}
          options={opts}
          disabled={triggerDisabled}
        />
      );
    }
    return (
      <div className="wbuc-relative">
        <NodeEditor
          className="wbuc-h-40 wbuc-overflow-auto"
          interactionMode="disabled"
          value={showValue}
          renderTagNode={renderTagNode}
        />
        <span
          className={cn(
            "wbuc-absolute wbuc-bottom-[0.625rem] wbuc-right-[0.625rem] wbuc-text-[#3B65EE]",
            triggerDisabled ? "wbuc-cursor-not-allowed" : "wbuc-cursor-pointer"
          )}
          role="button"
          onClick={click}
        >
          编辑
        </span>
      </div>
    );
  };
  // 弹窗标题
  const renderTitle = () => {
    switch (type) {
      case DATA_TYPE.DYNAMIC:
        return "赋值";
      case DATA_TYPE.FORMAT:
        return "文本编辑";
      case DATA_TYPE.FORMULA:
        return "自定义值编辑";
      default:
        return "编辑";
    }
  };
  // 字段选择器
  const renderFields = (
    data: ValueEditorProps["config"],
    mode: "single" | "multiple"
  ) => {
    const groupCount = data.length > 30 ? Math.ceil(data.length / 5) : 6;
    const group: ValueEditorProps["config"][] = chunk(data, groupCount);
    return group.map((i, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div
        className="wbuc-flex wbuc-max-w-[20%] wbuc-flex-1 wbuc-flex-col wbuc-gap-4"
        key={idx}
      >
        {i.map((j) => {
          // 非数字类型字段不显示在公式编辑器中
          const isNotNumber =
            type === DATA_TYPE.FORMULA && j.type !== VALUE_TYPE.NUMBER;
          const fieldDisabled =
            (typeof disabled === "function" && disabled(values, j)) ||
            isNotNumber ||
            j.disabled;
          const active =
            (mode === "single" && nodes[0]?.content === j.code) ||
            activeField === j.code;
          return (
            <Tooltip title={j.tooltip} key={j.code}>
              <span
                ref={(node) => {
                  fieldRefs.current[j.code] = node;
                }}
                className={cn(
                  "wbuc-w-fit wbuc-select-none wbuc-text-nowrap wbuc-rounded-[0.125rem] wbuc-border wbuc-border-solid wbuc-px-[0.375rem]",
                  active
                    ? "wbuc-border-[#3B65EE] wbuc-bg-[#3B65EE] wbuc-text-[#FFFFFF]"
                    : "wbuc-border-[rgba(25,28,38,0.1)] wbuc-bg-[#F9FAFB]",
                  fieldDisabled
                    ? "wbuc-cursor-not-allowed"
                    : "wbuc-cursor-pointer"
                )}
                onClick={() => {
                  if (fieldDisabled) return;
                  setActiveField(undefined);
                  if (mode === "single") {
                    setNodes([
                      {
                        type: "tag",
                        content: j.code,
                        id: "single",
                      },
                    ]);
                  } else {
                    nodeEditorRef.current?.insertNodeAtCursor({
                      type: "tag",
                      content: j.code,
                    });
                  }
                }}
              >
                {j.name}
              </span>
            </Tooltip>
          );
        })}
      </div>
    ));
  };
  // 选择器
  const renderSelector = (mode: "single" | "multiple") => {
    return (
      <Tabs
        className="-wbuc-mt-1"
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        items={searchResult.map((i) => ({
          label: i.name,
          key: i.code,
          children: (
            <div
              key={i.code}
              className="wbuc-mt-[0.625rem] wbuc-flex wbuc-flex-col wbuc-gap-4 wbuc-border-b-0 wbuc-border-l-0 wbuc-border-r-0 wbuc-border-t wbuc-border-solid wbuc-border-t-[rgba(25,28,38,0.06)] wbuc-pt-5"
            >
              {/* 是否为二级字段 */}
              {i.children[0]?.children.length > 0 ? (
                i.children.map((j) => (
                  <div
                    className="wbuc-flex wbuc-flex-col wbuc-gap-4"
                    key={j.code}
                  >
                    <DotLabel>{j.name}</DotLabel>
                    <div className="wbuc-flex wbuc-gap-4">
                      {renderFields(j.children, mode)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="wbuc-flex wbuc-gap-4">
                  {renderFields(i.children, mode)}
                </div>
              )}
            </div>
          ),
        }))}
        size={"small"}
      />
    );
  };
  // 搜索框
  const renderSearch = () => {
    return (
      <Search
        value={search}
        placeholder="请输入搜索内容"
        style={{ width: "100%" }}
        onChange={setSearch}
        allowClear
      />
    );
  };
  // 节点编辑器
  const renderNodeEditor = (mode?: NodeEditorProps["interactionMode"]) => {
    return (
      <div className="wbuc-flex wbuc-flex-col">
        <NodeEditor
          ref={nodeEditorRef}
          className="wbuc-h-[12.5rem] wbuc-overflow-auto"
          interactionMode={mode}
          mergeable={(prev, cur) =>
            isNumberTag(prev, fieldsCodes) && isNumberTag(cur, fieldsCodes)
          }
          value={nodes}
          onChange={(n) => {
            setActiveField(undefined);
            setRequiredError(required && n.length === 0);
            setNodes(n);
          }}
          nodeClassName={(node) =>
            cn(
              "wbuc-cursor-pointer",
              errorIds.includes(node.id) && "wbuc-text-[#FF2A2A]",
              activeField === node.content && "wbuc-border-[#3B65EE]"
            )
          }
          renderTagNode={renderTagNode}
          onNodeClick={setActiveNode}
        />
        {errorIds.length > 0 && (
          <span className="wbuc-text-[#FF2A2A]">请输入正确的公式</span>
        )}
        {requiredError && (
          <span className="wbuc-text-[#FF2A2A]">请输入内容</span>
        )}
      </div>
    );
  };
  // 计算机键盘
  const renderInput = () => {
    return (
      <div className="wbuc-grid wbuc-h-[12.5rem] wbuc-w-[10rem] wbuc-select-none wbuc-grid-cols-4 wbuc-grid-rows-5 wbuc-rounded-[0.25rem] wbuc-border wbuc-border-solid wbuc-border-[rgba(25,28,38,0.1)]">
        {formulaInputs.map((i) => (
          <div
            key={i.label}
            className={cn(
              "wbuc-flex wbuc-cursor-pointer wbuc-items-center wbuc-justify-center wbuc-text-xs",
              ["bracket", "operator", "delete"].includes(i.type)
                ? "wbuc-bg-[rgba(59,101,238,0.04)]"
                : "wbuc-bg-[#FFFFFF]"
            )}
            style={{
              gridColumn: `span ${i.span || 1}`,
              color: i.color,
            }}
            onClick={() => {
              setActiveField(undefined);
              if (i.type === "delete") {
                nodeEditorRef.current?.deleteNodeAtCursor();
              } else {
                nodeEditorRef.current?.insertNodeAtCursor({
                  type: "tag",
                  content: i.label,
                });
              }
            }}
            role="button"
          >
            {i.label}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Modal
        open={open}
        title={renderTitle()}
        onCancel={() => setOpen(false)}
        width="52.5rem"
        okText="确定"
        cancelText="取消"
        onOk={() => {
          const nodesErr = errorIds.length > 0;
          const requiredErr = required && values.length === 0;
          if (nodesErr || requiredErr) {
            return;
          }
          onChange?.(values);
          setOpen(false);
        }}
      >
        <div className="wbuc-flex wbuc-flex-col wbuc-gap-5 wbuc-overflow-hidden wbuc-p-[1.875rem]">
          {children}
          {type === DATA_TYPE.FORMAT && renderNodeEditor()}
          {type === DATA_TYPE.FORMULA && (
            <div className="wbuc-flex wbuc-gap-5">
              <div className="wbuc-flex-1">
                {renderNodeEditor("cursorOnly")}
              </div>
              <div className="wbuc-flex-shrink-0">{renderInput()}</div>
            </div>
          )}
          {isDynamic ? (
            renderSearch()
          ) : (
            <div className="wbuc-flex wbuc-items-center wbuc-justify-between">
              <span className="wbuc-text-base wbuc-font-[500]">插入参数</span>
              <div className="wbuc-w-[18.75rem]">{renderSearch()}</div>
            </div>
          )}
          {renderSelector(isDynamic ? "single" : "multiple")}
        </div>
      </Modal>
    </>
  );
};

export default ValueEditor;
